import React, { useState } from 'react';
import { CalendarIcon, MapPinIcon, ClockIcon, UsersIcon, TagIcon, ChevronRightIcon, SendIcon, CheckIcon, XIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageTransition } from '../components/layout/PageTransition';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input, TextArea } from '../components/ui/Input';
import { DecorativeShape } from '../components/DecorativeShape';
import { Animate } from '../components/ui/Animate';
import { useLanguage } from '../context/LanguageContext';

// ─────────────────────────────────────────────────────────────
// FORM EMAIL — same as Contact page
// ─────────────────────────────────────────────────────────────
const FORM_EMAIL = 'helgi@helgig.com';

// ─────────────────────────────────────────────────────────────
// EVENT DATA
// Update this list to add/remove/modify scheduled events.
//
// priceIS / priceEN: shown on the card, e.g. "49.900 kr." or "Contact for pricing"
// status: 'open' | 'few-left' | 'full' | 'coming-soon'
// ─────────────────────────────────────────────────────────────
interface ScheduledEvent {
  id: string;
  titleIS: string;
  titleEN: string;
  dateIS: string;
  dateEN: string;
  timeIS: string;
  timeEN: string;
  locationIS: string;
  locationEN: string;
  formatIS: string;
  formatEN: string;
  priceIS: string;
  priceEN: string;
  spotsTotal: number | null;
  spotsLeft: number | null;
  status: 'open' | 'few-left' | 'full' | 'coming-soon';
  internalPath: string;
  tagIS: string;
  tagEN: string;
  accentColor: string;
  accentText: string;
  accentLight: string;
}
const events: ScheduledEvent[] = [{
  id: 'ftm-autumn-2026',
  titleIS: 'Stjórnandi í Fyrsta Sinn',
  titleEN: 'First-Time Manager',
  dateIS: 'Haust 2026 - dagsetning í vinnslu',
  dateEN: 'Autumn 2026 - date to be confirmed',
  timeIS: '',
  timeEN: '',
  locationIS: 'Á netinu (Zoom)',
  locationEN: 'Online (Zoom)',
  formatIS: '6 vikur · Ein lota á viku',
  formatEN: '6 weeks · One session per week',
  priceIS: 'Hafðu samband',
  priceEN: 'Contact for pricing',
  spotsTotal: null,
  spotsLeft: null,
  status: 'coming-soon',
  internalPath: '/services/courses/first-time-manager',
  tagIS: 'Námskeið',
  tagEN: 'Course',
  accentColor: 'bg-coral',
  accentText: 'text-coral',
  accentLight: 'bg-coral/10'
}, {
  id: 'conflict-autumn-2026',
  titleIS: 'Conflict Coaching',
  titleEN: 'Conflict Coaching',
  dateIS: 'Haust 2026 - dagsetning í vinnslu',
  dateEN: 'Autumn 2026 - date to be confirmed',
  timeIS: '',
  timeEN: '',
  locationIS: 'Reykjavík (staðfest við skráningu)',
  locationEN: 'Reykjavík (venue confirmed on booking)',
  formatIS: '2 dagar · Millibil milli daga',
  formatEN: '2 days · Practice interval between',
  priceIS: 'Hafðu samband',
  priceEN: 'Contact for pricing',
  spotsTotal: null,
  spotsLeft: null,
  status: 'coming-soon',
  internalPath: '/services/courses/conflict-coaching',
  tagIS: 'Námskeið',
  tagEN: 'Course',
  accentColor: 'bg-teal',
  accentText: 'text-teal',
  accentLight: 'bg-teal/10'
}, {
  id: 'meetings-autumn-2026',
  titleIS: 'Betri Fjarfundir',
  titleEN: 'Better Online Meetings',
  dateIS: 'Haust 2026 - dagsetning í vinnslu',
  dateEN: 'Autumn 2026 - date to be confirmed',
  timeIS: '',
  timeEN: '',
  locationIS: 'Á netinu (Zoom)',
  locationEN: 'Online (Zoom)',
  formatIS: '2 x 2 klst',
  formatEN: '2 x 2 hrs',
  priceIS: 'Hafðu samband',
  priceEN: 'Contact for pricing',
  spotsTotal: null,
  spotsLeft: null,
  status: 'coming-soon',
  internalPath: '/services/courses/better-online-meetings',
  tagIS: 'Námskeið',
  tagEN: 'Course',
  accentColor: 'bg-blue-600',
  accentText: 'text-blue-600',
  accentLight: 'bg-blue-50'
}];

// ─────────────────────────────────────────────────────────────
// Registration modal / inline form
// ─────────────────────────────────────────────────────────────
interface RegFormProps {
  event: ScheduledEvent;
  isIS: boolean;
  onClose: () => void;
}
function RegistrationForm({
  event,
  isIS,
  onClose
}: RegFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const title = isIS ? event.titleIS : event.titleEN;
  const date = isIS ? event.dateIS : event.dateEN;
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(p => ({
      ...p,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setHasError(false);
    try {
      const res = await fetch(`https://formsubmit.co/ajax/${FORM_EMAIL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message || '—',
          _subject: `Course registration interest: ${title}`,
          course: title,
          date
        })
      });
      if (res.ok) {
        setIsSubmitted(true);
      } else {
        setHasError(true);
      }
    } catch {
      setHasError(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-id="element-981" data-mp="3oortoj0yj6f4">
      <Animate data-id="element-982" data-mp="6ak48bbdodpx">
        <Card className="w-full max-w-md p-8 relative shadow-2xl" data-id="element-983" data-mp="6b25jmcwdi4p">
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close" data-id="element-984" data-mp="2bba35cfedtu">
            <XIcon size={20} data-id="element-985" data-mp="bbpy4cgry711" />
          </button>

          {isSubmitted ? <div className="text-center py-6" data-id="element-986" data-mp="3536ec2f3z7g7">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-987" data-mp="354hiw4gdoj8d">
                <CheckIcon size={28} strokeWidth={2.5} data-id="element-988" data-mp="3925cn46c45yo" />
              </div>
              <h3 className="text-xl font-bold text-charcoal mb-2" data-id="element-989" data-mp="3e29w932jhgt4">
                {isIS ? 'Skráning móttekin!' : 'Got it!'}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-6" data-id="element-990" data-mp="3nto01n1ix2s2">
                {isIS ? 'Ég kem til baka til þín þegar dagsetning er staðfest.' : "I'll be in touch as soon as the date is confirmed."}
              </p>
              <Button variant="outline" onClick={onClose} size="sm" data-id="element-991" data-mp="mlp8lschhrj">
                {isIS ? 'Loka' : 'Close'}
              </Button>
            </div> : <>
              <div className={`inline-block px-3 py-1 rounded-full text-xs font-bold mb-4 ${event.accentLight} ${event.accentText}`} data-id="element-992" data-mp="22hmepkhs51q6">
                {isIS ? event.tagIS : event.tagEN}
              </div>
              <h3 className="text-xl font-bold text-charcoal mb-1" data-id="element-993" data-mp="1zwy4rogpbioi">{title}</h3>
              <p className="text-sm text-gray-500 mb-6" data-id="element-994" data-mp="1v45010l8gacf">
                {isIS ? 'Láttu mig vita og ég kem til baka þegar dagsetning er staðfest.' : 'Leave your details and I will contact you when the date is confirmed.'}
              </p>

              {hasError && <div className="p-3 bg-red-50 text-red-700 rounded-xl text-sm mb-4 border border-red-100" data-id="element-995" data-mp="2iv0xad8gjxr2">
                  {isIS ? 'Eitthvað fór úrskeiðis. Sendu beint á ' : 'Something went wrong. Email directly: '}
                  <a href={`mailto:${FORM_EMAIL}`} className="underline font-bold" data-id="element-996" data-mp="28va4qh3gjstm">{FORM_EMAIL}</a>
                </div>}

              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-997" data-mp="24wssjj56js5c">
                <Input label={isIS ? 'Nafn' : 'Name'} name="name" placeholder={isIS ? 'Jón Jónsson' : 'Jane Smith'} value={formData.name} onChange={handleChange} required data-id="element-998" data-mp="xhxvtrsuck0l" />
                <Input label={isIS ? 'Netfang' : 'Email'} name="email" type="email" placeholder={isIS ? 'jon@example.com' : 'jane@example.com'} value={formData.email} onChange={handleChange} required data-id="element-999" data-mp="2xk75tffs0mjj" />
                <TextArea label={isIS ? 'Spurningar eða athugasemdir (valfrjálst)' : 'Questions or comments (optional)'} name="message" rows={3} placeholder={isIS ? 'Hér má setja spurningar eða sérstakar óskir...' : 'Any questions or special requirements...'} value={formData.message} onChange={handleChange} data-id="element-1000" data-mp="3cao81108jghh" />
                <Button type="submit" className="w-full justify-center" size="lg" isLoading={isSubmitting} rightIcon={<SendIcon size={16} data-id="element-1002" data-mp="968l7sgxrey2" />} data-id="element-1001" data-mp="3sqmg0w9qzyk">
                  {isIS ? 'Láta vita' : 'Notify me'}
                </Button>
              </form>
            </>}
        </Card>
      </Animate>
    </div>;
}

// ─────────────────────────────────────────────────────────────
// Status badge
// ─────────────────────────────────────────────────────────────
function StatusBadge({
  event,
  isIS
}: {
  event: ScheduledEvent;
  isIS: boolean;
}) {
  const map = {
    open: {
      label: isIS ? 'Skráning opin' : 'Open',
      classes: 'bg-green-100 text-green-700'
    },
    'few-left': {
      label: isIS ? 'Fá pláss eftir' : 'Few spots left',
      classes: 'bg-amber-100 text-amber-700'
    },
    full: {
      label: isIS ? 'Fullskráð' : 'Full',
      classes: 'bg-red-100 text-red-700'
    },
    'coming-soon': {
      label: isIS ? 'Í vinnslu' : 'Coming soon',
      classes: 'bg-gray-100 text-gray-500'
    }
  };
  const s = map[event.status];
  return <span className={`text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap ${s.classes}`} data-id="element-1003" data-mp="1dmdievki5xl1">
      {s.label}
    </span>;
}

// ─────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────
export function Events() {
  const {
    language
  } = useLanguage();
  const isIS = language === 'is';
  const [activeRegistration, setActiveRegistration] = useState<ScheduledEvent | null>(null);
  return <PageTransition data-id="element-1004" data-mp="pwsvy2p54i70">
      {/* Registration modal */}
      {activeRegistration && <RegistrationForm event={activeRegistration} isIS={isIS} onClose={() => setActiveRegistration(null)} data-id="element-1005" data-mp="kxganmdx3j6j" />}

      <div className="pt-12 pb-24" data-id="element-1006" data-mp="fzn2wowyuh45">
        {/* Header */}
        <section className="text-center max-w-4xl mx-auto px-4 mb-16 relative" data-id="element-1007" data-mp="2fb7kalw8y31z">
          <DecorativeShape type="square" color="bg-coral/10" className="top-0 right-0 w-32 h-32 rotate-12 -z-10" data-id="element-1008" data-mp="2acd0phqggagt" />
          <DecorativeShape type="circle" color="bg-teal/10" className="bottom-0 left-0 w-24 h-24 -z-10" data-id="element-1009" data-mp="2itmzx54mpsq8" />
          <Animate data-id="element-1010" data-mp="2mugp01tt5dkp">
            <span className="inline-block px-4 py-2 bg-teal/10 text-teal font-medium rounded-full text-sm mb-6" data-id="element-1011" data-mp="2mtx9ssvjl3ad">
              {isIS ? 'Námskeið og vinnustofur' : 'Courses and workshops'}
            </span>
            <h1 className="text-5xl md:text-6xl font-bold mb-4" data-id="element-1012" data-mp="2wjgsnyj98x58">
              {isIS ? 'Næstu viðburðir' : 'Upcoming events'}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1013" data-mp="1zwvk55oapvqx">
              {isIS ? 'Námskeið og vinnustofur eru í undirbúningi fyrir haust 2026. Skráðu þig á biðlista og ég kem til baka þegar dagsetningar eru staðfestar.' : 'Courses and workshops are being scheduled for autumn 2026. Register your interest below and I will be in touch when dates are confirmed.'}
            </p>
          </Animate>
        </section>

        {/* Event cards */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8" data-id="element-1014" data-mp="3pbjznfs6ntt6">
          {events.map((event, i) => <Animate key={event.id} delay={i * 0.08} data-id="element-1015" data-mp="2um8nwah6e814" data-mpi={i}>
              <Card className="p-0 overflow-hidden border-2 border-gray-100 transition-colors" data-id="element-1016" data-mp="2ulp6cuvftlnm">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-0" data-id="element-1017" data-mp="39i6dtb0iag4w">
                  {/* Accent panel */}
                  <div className={`lg:col-span-3 ${event.accentLight} p-8 flex flex-col justify-center items-center text-center gap-3`} data-id="element-1018" data-mp="34l69p5n7vngf">
                    <div className={`w-14 h-14 rounded-full ${event.accentColor} text-white flex items-center justify-center shadow-md`} data-id="element-1019" data-mp="2bvzcqb3ev5xa">
                      <CalendarIcon size={24} data-id="element-1020" data-mp="2qlf1xmnan3av" />
                    </div>
                    <div data-id="element-1021" data-mp="2ltdfx90bpy7n">
                      <p className={`font-bold text-base ${event.accentText} leading-snug`} data-id="element-1022" data-mp="2lt53tjc0ywxi">
                        {isIS ? event.dateIS : event.dateEN}
                      </p>
                      {(isIS ? event.timeIS : event.timeEN) && <p className="text-xs text-gray-500 mt-1.5 flex items-center justify-center gap-1" data-id="element-1023" data-mp="8n754q4496g1">
                          <ClockIcon size={11} data-id="element-1024" data-mp="ddni6940yq1v" />
                          {isIS ? event.timeIS : event.timeEN}
                        </p>}
                    </div>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${event.accentLight} ${event.accentText}`} data-id="element-1025" data-mp="3uskla1wqez26">
                      {isIS ? event.tagIS : event.tagEN}
                    </span>
                  </div>

                  {/* Content panel */}
                  <div className="lg:col-span-9 p-8 bg-white flex flex-col justify-between gap-5" data-id="element-1026" data-mp="wc04yxnuzyfx">
                    <div data-id="element-1027" data-mp="icqp5z3kf6ch">
                      <div className="flex items-start justify-between gap-4 mb-3 flex-wrap" data-id="element-1028" data-mp="melo4wy0x7n3">
                        <h2 className="text-2xl font-bold text-charcoal" data-id="element-1029" data-mp="mggbehwrpbuc">
                          {isIS ? event.titleIS : event.titleEN}
                        </h2>
                        <StatusBadge event={event} isIS={isIS} data-id="element-1030" data-mp="376e00dyarmu0" />
                      </div>

                      <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-500" data-id="element-1031" data-mp="3ncqx5157iltb">
                        <span className="flex items-center gap-1.5" data-id="element-1032" data-mp="3ikh3ba0z11lr">
                          <MapPinIcon size={13} className="flex-shrink-0" data-id="element-1033" data-mp="3dlmmgdmkdn4a" />
                          {isIS ? event.locationIS : event.locationEN}
                        </span>
                        <span className="flex items-center gap-1.5" data-id="element-1034" data-mp="a5fgg0gofdsc">
                          <TagIcon size={13} className="flex-shrink-0" data-id="element-1035" data-mp="a4nvrkomb0gu" />
                          {isIS ? event.formatIS : event.formatEN}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-5 border-t border-gray-100" data-id="element-1036" data-mp="9408zdyu3sm">
                      <button onClick={() => setActiveRegistration(event)} className="inline-flex" data-id="element-1037" data-mp="2ky09ierg564r">
                        <Button variant="outline" rightIcon={<ChevronRightIcon size={15} data-id="element-1039" data-mp="wenpk0wdyvyz" />} data-id="element-1038" data-mp="wduongk3z0or">
                          {isIS ? 'Láta vita þegar skráning opnar' : 'Notify me when registration opens'}
                        </Button>
                      </button>

                      <Link to={event.internalPath} data-id="element-1040" data-mp="15czocq7ep02i">
                        <Button variant="outline" size="sm" data-id="element-1041" data-mp="1a5sqd6bhpuaw">
                          {isIS ? 'Um námskeiðið' : 'About the course'}
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </Card>
            </Animate>)}
        </section>

        {/* Payment note */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-8" data-id="element-1042" data-mp="1o2xrqt09e4zt">
          <Animate data-id="element-1043" data-mp="1t3lhlbcr4fsg">
            <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-2xl p-5" data-id="element-1044" data-mp="1t20ychvw0j9w">
              <div className="w-5 h-5 rounded-full bg-gray-300 text-white flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold" data-id="element-1045" data-mp="3pyhuuw8b9r6o">i</div>
              <p className="text-sm text-gray-600" data-id="element-1046" data-mp="1221e6rbskpv6">
                {isIS ? 'Við staðfestingu dagsetningar fær þú tölvupóst með skráningarleiðbeiningum og greiðsluupplýsingum. Greiðsla fer fram með reikningi eða bankamillifærslu. Við erfiðleika, hafðu samband beint á ' : 'When dates are confirmed you will receive an email with registration instructions and payment details. Payment is by invoice or bank transfer. For any questions, contact me directly at '}
                <a href={`mailto:${FORM_EMAIL}`} className="text-teal font-medium hover:underline" data-id="element-1047" data-mp="1fzpzu1doo11a">{FORM_EMAIL}</a>
              </p>
            </div>
          </Animate>
        </section>

        {/* Custom / in-house CTA */}
        <section className="max-w-4xl mx-auto px-4 mt-16" data-id="element-1048" data-mp="1pr44sooelt1t">
          <Animate data-id="element-1049" data-mp="23ptz5wju9emh">
            <Card className="p-10 bg-gradient-to-br from-teal/5 to-coral/5 border-0 text-center" data-id="element-1050" data-mp="23r55o44o50ha">
              <h2 className="text-2xl font-bold mb-3" data-id="element-1051" data-mp="1ysli5zaffgmt">
                {isIS ? 'Ertu að leita að sérsniðinni þjálfun?' : 'Looking for something tailored?'}
              </h2>
              <p className="text-gray-600 mb-6 max-w-lg mx-auto" data-id="element-1052" data-mp="2ncovdsz7w4ht">
                {isIS ? 'Ég hanna og held námskeið og vinnustofur innanhúss hjá fyrirtækjum - aðlagað að ykkar samhengi og þörfum.' : 'I design and run in-house courses and workshops for organisations - adapted to your context and needs.'}
              </p>
              <Link to="/contact" data-id="element-1053" data-mp="1zkhtyfemx7bj">
                <Button variant="secondary" rightIcon={<ChevronRightIcon size={16} data-id="element-1055" data-mp="24eueytvreqpe" />} data-id="element-1054" data-mp="24dt4aqino687">
                  {isIS ? 'Hafa samband' : 'Get in touch'}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}